import { ArrowRightLeft, Check, ChevronDown, Save, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState, useMemo, useRef, useEffect } from 'react';
import Button from '../../../components/Button';
import MarkdownEditor from './MarkdownEditor';
import MarkdownPreview from './MarkdownPreview';
import type { QuestionOption } from '../services/learningTypes';
import { getIdValidationError, sanitizeId } from '../../../utils/idValidation';

type BadgeType = 'learning' | 'question' | 'remember' | 'correct' | 'incorrect';

// Predefined button text options with categories
const BUTTON_TEXT_OPTIONS = [
    { category: 'Acknowledgment / Neutral', options: ['Alright', 'Okay', 'Got it', 'Yep', 'Yeah okay', 'True', 'Exactly'] },
    { category: 'Understanding / Clarity', options: ['I understand', 'That makes sense', 'Makes sense', 'That clicks', 'Oh, okay', 'Ah, okay', 'Makes sense now', 'That explains it', 'Good to know'] },
    { category: 'Interest / Curiosity', options: ['Interesting', 'Hmm', 'Ohhh', 'Curious', 'Tell me more', 'Show me'] },
    { category: 'Empathy / Relatability', options: ['That\'s rough', 'That\'s a lot', 'Oof, yeah', 'Ugh, true', 'Been there', 'Yep, familiar'] },
    { category: 'Enthusiasm / Positive', options: ['Alright!', 'Wow!', 'This!', 'Ah, Yes!', 'Cool stuff', 'Brain Plasticity!'] },
    { category: 'Gratitude / Appreciation', options: ['Thanks!', 'Alright, thanks!', 'That helps', 'Needed that!', 'That\'s useful!', 'I\'ll remember!'] },
    { category: 'Willingness / Commitment', options: ['I can try', 'I can do that', 'I\'d like that!', 'Would be great', 'I\'m ready'] },
    { category: 'Questions / Seeking Clarification', options: ['Explain?', 'That helps?', 'It doesn\'t?', 'Wait, really?', 'Why does that matter?', 'So what now?'] },
    { category: 'Contemplative / Reflective', options: ['Hmm...', 'Interesting...', 'Makes Sense...', 'Yup...', 'Yep...', 'Been there...'] }
] as const;

interface BasePageProps {
    uid?: string;
    order: number;
    buttonText?: string;
    content: string;
}

interface BasePageEditorProps<T extends BasePageProps> {
    page: T;
    pageNumber: number;
    pageType?: string;
    currentType: 'page' | 'question';
    onUpdate: (field: keyof T, value: string | number | boolean | QuestionOption[] | string[]) => void;
    onDelete: () => void;
    onSave?: () => Promise<void>;
    onConvertType?: () => void;
    saveStatus?: 'saved' | 'unsaved' | 'saving' | 'error';
    markdownPreviewProps?: {
        isQuestionPage?: boolean;
        options?: QuestionOption[];
        correctOptionIds?: string[];
    };
    additionalFields?: ReactNode;
    editorRows?: number;
}

export default function BasePageEditor<T extends BasePageProps>({
    page,
    pageNumber,
    pageType = 'Page',
    currentType,
    onUpdate,
    onDelete,
    onSave,
    onConvertType,
    saveStatus,
    markdownPreviewProps = { isQuestionPage: false },
    additionalFields,
    editorRows = 24
}: BasePageEditorProps<T>) {
    const [isLoading, setIsLoading] = useState(false);
    const [isButtonTextDropdownOpen, setIsButtonTextDropdownOpen] = useState(false);
    const buttonTextDropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (buttonTextDropdownRef.current && !buttonTextDropdownRef.current.contains(event.target as Node)) {
                setIsButtonTextDropdownOpen(false);
            }
        };

        if (isButtonTextDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isButtonTextDropdownOpen]);

    // Detect current badge type from content
    const currentBadgeType = useMemo((): BadgeType | null => {
        const badgeTypes: BadgeType[] = ['learning', 'question', 'remember', 'correct', 'incorrect'];
        for (const badge of badgeTypes) {
            // Match ```badgetype\nAny Text\n```
            const regex = new RegExp(`\`\`\`${badge}\\s*\\n[^\\n]*\\n\`\`\``, 'i');
            if (regex.test(page.content)) {
                return badge;
            }
        }
        return null;
    }, [page.content]);

    const handleBadgeTypeChange = (newBadgeType: BadgeType) => {
        let updatedContent = page.content;

        // Replace existing badge type while preserving the text inside
        const badgeTypes: BadgeType[] = ['learning', 'question', 'remember', 'correct', 'incorrect'];
        for (const badge of badgeTypes) {
            // Match ```badgetype\nAny Text\n``` and capture the text
            const regex = new RegExp(`\`\`\`${badge}\\s*\\n([^\\n]*)\\n\`\`\``, 'gi');
            if (regex.test(updatedContent)) {
                // Replace while preserving the inner text
                updatedContent = updatedContent.replace(regex, (_match, innerText) => {
                    return `\`\`\`${newBadgeType}\n${innerText}\n\`\`\``;
                });
                break;
            }
        }

        onUpdate('content' as keyof T, updatedContent);
    };

    const handleToggleEmphasis = (startIndex: number, endIndex: number) => {
        const content = page.content;

        // Expand selection to the next whitespace at boundaries
        let adjustedStartIndex = startIndex;
        let adjustedEndIndex = endIndex;

        // Check if character is whitespace or underscore
        const isWhitespace = (char: string) => /\s/.test(char);

        // Expand start index backwards to the previous whitespace (or start of string)
        while (adjustedStartIndex > 0 && !isWhitespace(content[adjustedStartIndex - 1]) && content[adjustedStartIndex - 1] !== '_') {
            adjustedStartIndex--;
        }

        // Expand end index forwards to the next whitespace (or end of string)
        while (adjustedEndIndex < content.length && !isWhitespace(content[adjustedEndIndex]) && content[adjustedEndIndex] !== '_') {
            adjustedEndIndex++;
        }

        // Get the expanded text
        const expandedText = content.substring(adjustedStartIndex, adjustedEndIndex);

        // Check if the expanded text is wrapped in underscores
        const hasUnderscores = expandedText.startsWith('_') && expandedText.endsWith('_') && expandedText.length > 2;

        let updatedContent: string;

        if (hasUnderscores) {
            // Remove the underscores from the text
            const textWithoutUnderscores = expandedText.substring(1, expandedText.length - 1);
            updatedContent = content.substring(0, adjustedStartIndex) + textWithoutUnderscores + content.substring(adjustedEndIndex);
        } else {
            // Add underscores around the text
            updatedContent = content.substring(0, adjustedStartIndex) + '_' + expandedText + '_' + content.substring(adjustedEndIndex);
        }

        onUpdate('content' as keyof T, updatedContent);
    };

    const handleResetEmphasis = () => {
        // Remove ALL underscore characters from the content
        const updatedContent = page.content.replace(/_/g, '');
        onUpdate('content' as keyof T, updatedContent);
    };

    const handleSave = async () => {
        if (!onSave) return;

        setIsLoading(true);
        try {
            await onSave();
        } catch (error) {
            console.error('Error saving page:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getSaveButtonContent = () => {
        if (isLoading || saveStatus === 'saving') {
            return { icon: null, text: 'Saving...', variant: 'secondary' as const };
        }
        if (saveStatus === 'saved') {
            return { icon: <Check size={16} />, text: 'Saved', variant: 'success' as const };
        }
        if (saveStatus === 'error') {
            return { icon: <Save size={16} />, text: 'Retry Save', variant: 'danger' as const };
        }
        return { icon: <Save size={16} />, text: 'Save Page', variant: 'primary' as const };
    };

    const saveButtonContent = getSaveButtonContent();

    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-3">
                    <h5 className="font-medium text-gray-900">{pageType} {pageNumber}</h5>
                    {saveStatus === 'unsaved' && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                            Unsaved changes
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {onSave && (
                        <Button
                            type="button"
                            onClick={handleSave}
                            variant={saveButtonContent.variant}
                            size="sm"
                            loading={isLoading || saveStatus === 'saving'}
                            disabled={isLoading || saveStatus === 'saving' || saveStatus === 'saved'}
                            icon={saveButtonContent.icon}
                        >
                            {saveButtonContent.text}
                        </Button>
                    )}
                    {onConvertType && (
                        <Button
                            type="button"
                            onClick={onConvertType}
                            variant="outline"
                            size="sm"
                            icon={<ArrowRightLeft size={16} />}
                            title={currentType === 'question' ? 'Convert to regular page' : 'Convert to question page'}
                        >
                            {currentType === 'question' ? 'To Page' : 'To Question'}
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={onDelete}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-800 transition-colors duration-200"
                        icon={<Trash2 size={16} />}
                    />
                </div>
            </div>

            <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Page ID
                        </label>
                        <input
                            type="text"
                            value={page.uid || ''}
                            onChange={(e) => onUpdate('uid' as keyof T, sanitizeId(e.target.value))}
                            placeholder="e.g., can-i-recover-from-ocd"
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${getIdValidationError(page.uid || '') ? 'border-red-300' : 'border-gray-300'
                                }`}
                        />
                        {getIdValidationError(page.uid || '') && (
                            <p className="text-xs text-red-600 mt-1">
                                {getIdValidationError(page.uid || '')}
                            </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1"> Leave empty for auto-generated ID. No spaces allowed - use hyphens instead.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Order
                        </label>
                        <input
                            type="number"
                            value={page.order}
                            onChange={(e) => onUpdate('order' as keyof T, parseInt(e.target.value) || 1)}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Button Text
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={page.buttonText || ''}
                                onChange={(e) => onUpdate('buttonText' as keyof T, e.target.value)}
                                placeholder="e.g., Continue, Next"
                                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <div ref={buttonTextDropdownRef} className="absolute right-1 top-1 bottom-1">
                                <button
                                    type="button"
                                    onClick={() => setIsButtonTextDropdownOpen(!isButtonTextDropdownOpen)}
                                    className="h-full px-2 rounded hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                                    title="Select from presets"
                                >
                                    <ChevronDown size={16} className="text-gray-500" />
                                </button>
                                {isButtonTextDropdownOpen && (
                                    <div className="absolute right-0 mt-1 w-64 sm:w-72 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                                        {BUTTON_TEXT_OPTIONS.map((group) => (
                                            <div key={group.category} className="border-b border-gray-200 last:border-b-0">
                                                <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                                    {group.category}
                                                </div>
                                                <div>
                                                    {group.options.map((option) => (
                                                        <button
                                                            key={option}
                                                            type="button"
                                                            onClick={() => {
                                                                onUpdate('buttonText' as keyof T, option);
                                                                setIsButtonTextDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 min-w-0">
                        <MarkdownEditor
                            value={page.content}
                            onChange={(content) => onUpdate('content' as keyof T, content)}
                            folder="content"
                            rows={editorRows}
                        />

                        {/* Badge Type Selector */}
                        <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Page Badge Type
                            </label>
                            <select
                                value={currentBadgeType || ''}
                                onChange={(e) => handleBadgeTypeChange(e.target.value as BadgeType)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                disabled={!currentBadgeType}
                            >
                                <option value="" disabled>
                                    {currentBadgeType ? 'Select badge type' : 'No badge found in content'}
                                </option>
                                <option value="learning">Learning</option>
                                <option value="question">Question</option>
                                <option value="remember">Remember</option>
                                <option value="correct">Correct</option>
                                <option value="incorrect">Incorrect</option>
                            </select>
                            {!currentBadgeType && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Add a badge in the format: ```badgetype\nYour Text\n```
                                </p>
                            )}
                            {currentBadgeType && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Current badge: <span className="font-semibold capitalize">{currentBadgeType}</span> (text inside will be preserved when changing type)
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <div className="w-full md:w-[375px] h-[812px] border border-gray-300 rounded-[20px] overflow-hidden bg-white shadow-lg">
                            <div className="w-full h-full overflow-y-auto">
                                <MarkdownPreview
                                    markdown={page.content}
                                    buttonText={page.buttonText}
                                    isQuestionPage={markdownPreviewProps.isQuestionPage}
                                    options={markdownPreviewProps.options}
                                    correctOptionIds={markdownPreviewProps.correctOptionIds}
                                    onToggleEmphasis={handleToggleEmphasis}
                                    onResetEmphasis={handleResetEmphasis}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {additionalFields}
            </div>
        </div>
    );
} 